/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useRef } from 'react';
import magic1 from 'assets/images/gen-ai-image-landing/magic/1.png';
import magic2 from 'assets/images/gen-ai-image-landing/magic/2.png';
import magic3 from 'assets/images/gen-ai-image-landing/magic/3.png';
import magic4 from 'assets/images/gen-ai-image-landing/magic/4.png';
import magic5 from 'assets/images/gen-ai-image-landing/magic/5.png';
import magic6 from 'assets/images/gen-ai-image-landing/magic/6.png';
import styles from './Magic.module.scss';

export default function Magic() {
  const sectionRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !textRef.current) return;

      const section = sectionRef.current;
      const text = textRef.current;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Calculate scroll progress through the section
      let progress = 0;

      if (rect.bottom > 0 && rect.top < viewportHeight) {
        // Section is visible in viewport
        if (rect.top <= 0 && rect.bottom >= viewportHeight) {
          // Section is fully in view or extends beyond viewport
          const scrolledIntoSection = Math.abs(rect.top);
          progress = Math.min(scrolledIntoSection / (sectionHeight - viewportHeight), 1);
        } else if (rect.top > 0) {
          // Section is entering from bottom - start transition when more visible
          const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          progress = Math.max(0, (visibleHeight / viewportHeight) * 0.02); // Very small progress when entering
        } else if (rect.bottom < viewportHeight) {
          // Section is leaving view
          progress = 1;
        }
      } else {
        // Section is not visible
        progress = rect.top > 0 ? 0 : 1;
      }

      // Apply blur based on progress (blur at start and end)
      let blurAmount = 0;
      let opacity = 1;

      if (progress < 0.015) {
        // Entering section - blur to focus (short transition)
        blurAmount = (0.015 - progress) * 667; // 0-10px blur
        opacity = 0.3 + (progress * 47); // 0.3-1 opacity
      } else if (progress > 0.85) {
        // Leaving section - focus to blur (later transition)
        blurAmount = (progress - 0.85) * 66.67; // 0-10px blur
        opacity = 1 - ((progress - 0.85) * 4.67); // 1-0.3 opacity
      }

      text.style.filter = `blur(${blurAmount}px)`;
      text.style.opacity = opacity;
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div ref={sectionRef} className={styles.magicSection}>
      <div ref={textRef} className={styles.stickyContent}>
        <div className={styles.magicText}>
          <div className={styles.line1}>MAKE MAGIC</div>
          <div className={styles.line2}>HAPPENS</div>
        </div>
      </div>

      {/* First screen height - Image 1 (top center) */}
      <div className={styles.screenHeight}>
        <div className={styles.imageContainer1}>
          <img src={magic1} alt="Coastal living room" className={styles.image1} />
          <p className={styles.imageText1}>It should be a living room with coastal</p>
        </div>
      </div>

      {/* Second screen height - Images 2, 3 */}
      <div className={styles.screenHeight}>
        <div className={styles.imageContainer2}>
          <img src={magic2} alt="Industrial bedroom" className={styles.image2} />
          <p className={styles.imageText2}>
            Hey show my bedroom
            <br />
            in industry style
          </p>
        </div>
        <div className={styles.imageContainer3}>
          <img src={magic3} alt="Farmhouse kitchen" className={styles.image3} />
          <p className={styles.imageText3}>Make my kitchen be a farmhouse</p>
        </div>
      </div>

      {/* Third screen height - Images 4, 5, 6 */}
      <div className={styles.screenHeight}>
        <div className={styles.imageContainer4}>
          <img src={magic4} alt="Standard bedroom" className={styles.image4} />
          <p className={styles.imageText4}>I just need a standard bedroom</p>
        </div>
        <div className={styles.imageContainer5}>
          <img src={magic5} alt="Modern bathroom" className={styles.image5} />
          <p className={styles.imageText5}>Make my bathroom more modern</p>
        </div>
        <div className={styles.imageContainer6}>
          <img src={magic6} alt="Luxury living room" className={styles.image6} />
          <p className={styles.imageText6}>
            It will be a luxury
            <br />
            living room
          </p>
        </div>
      </div>
    </div>
  );
}
